'use client';

import { LogoWithoutText } from '@c/icons/logoWithoutText';
import { useQuery } from '@tanstack/react-query';
import { getChatById, uploadChatImage } from '@util/firestore/messages';
import { getProductById } from '@util/firestore/products';
import { getPublicUserDoc } from '@util/firestore/users';
import { StatusLabel, useOnlineStatus } from '@util/hooks/useOnlineStatus';
import { getCdnImageUrls } from '@util/urlHelpers';
import { anyAreHeic, convertHeic } from '@util/heic';
import { logError } from '@util/logError';
import { useToastContext } from 'context/ToastContext';
import type { ChatDocument, ChatMessageDocument } from 'models/chat';
import { onSnapshot } from 'firebase/firestore';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import MessageBubble from '@c/dashboard/MessageBubble';
import { useChat } from 'context/ChatContext';
import { useAuth } from 'context/AuthContext';
import React from 'react';
import {
  ChevronRightIcon,
  ChevronLeftIcon,
  CloudUploadIcon,
} from 'lucide-react';
import { motion, LayoutGroup } from 'framer-motion';
import { cn, isMobile, isTablet } from '@util/index';
import SupportActionsRenderer from '@c/dashboard/components/SupportActionsRenderer';
import ChatDetailsModal from './ChatDetails/ChatDetailsModal';
import ChatPreviewSidebar from './ChatPreview/ChatPreviewSidebar';
import { getOrderById } from '@util/firestore/orders';
import ProductViewerModal from '@c/modals/ProductViewerModal';
import ChatInput from './ChatInput';
import { getOfferById } from '@util/firestore/offers';
import MobileChatDetailsModal from './ChatDetails/MobileChatDetailsModal';
import MobileChatPreview from './ChatPreview/MobileChatPreview';
import Avatar from '@ui/Avatar';
import { useOffers } from '@c/dashboard/useOffers';

type ChatWindowProps = {
  chat: ChatDocument;
  searchTerm?: string;
  onChatArchived?: () => void;
  onGoBack?: () => void;
  isSidebar?: boolean;
};

export default function ChatWindow({
  chat,
  searchTerm,
  onChatArchived,
  onGoBack,
  isSidebar,
}: ChatWindowProps) {
  const dragCounter = useRef<number>(0);
  const [messages, setMessages] = useState<ChatMessageDocument[] | null>(null);
  const [realtimeChat, setRealtimeChat] = useState<ChatDocument>(chat);
  const [showChatDetailsModal, setShowChatDetailsModal] = useState(false);
  const [showMobileChatDetailsModal, setShowMobileChatDetailsModal] =
    useState(false);
  const [inputMessage, setInputMessage] = useState<string>('');
  const [fileList, setFileList] = useState<File[]>([]);
  const [convertingImages, setConvertingImages] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [loadedChatPreviews, setLoadedChatPreviews] = useState<Set<string>>(
    new Set()
  );
  const [imageModal, setImageModal] = useState<{
    isOpen: boolean;
    index: number;
    images: { full: string; thumb: string; download_url?: string }[];
  }>({ isOpen: false, index: 0, images: [] });
  const messageWindowRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const inputId = useRef<string>(Math.random().toString(36).substring(7));
  const chatInputRef = useRef<HTMLTextAreaElement>(null);

  const { sendMessage } = useChat();
  const { userDoc } = useAuth();
  const { showErrorToast } = useToastContext();

  const { data: sellerDoc, isLoading: isSellerLoading } = useQuery({
    queryKey: ['publicUser', chat.seller_id],
    queryFn: () => getPublicUserDoc({ uid: chat.seller_id }),
    enabled: !!chat.seller_id,
  });

  const { data: buyerDoc, isLoading: isBuyerLoading } = useQuery({
    queryKey: ['publicUser', chat.buyer_id],
    queryFn: () => getPublicUserDoc({ uid: chat.buyer_id }),
    enabled: !!chat.buyer_id,
  });

  const { data: product, isLoading: isProductLoading } = useQuery({
    queryKey: ['product', chat?.product_id ?? ''],
    queryFn: () => getProductById(chat?.product_id ?? ''),
    enabled: !!chat?.product_id,
  });

  const { offer, isLoading: isOfferLoading } = useOffers(chat?.offer_id);

  const { data: order, isLoading: isOrderLoading } = useQuery({
    queryKey: ['order', chat?.order_id ?? ''],
    queryFn: () => getOrderById(chat?.order_id ?? ''),
    enabled: !!chat?.order_id,
  });

  // Find matching messages based on search term
  const matchingMessageIndices = useMemo(() => {
    if (!searchTerm || !messages) return [];

    const indices: number[] = [];
    const lowercaseSearchTerm = searchTerm.toLowerCase();

    messages.forEach((message, index) => {
      if (message.content?.toLowerCase().includes(lowercaseSearchTerm)) {
        indices.push(index);
      }
    });

    return indices;
  }, [messages, searchTerm]);

  // Extract images from chat messages
  const chatImages = useMemo(() => {
    if (!messages) return [];

    return messages
      .filter((message) => message.is_img && message.content)
      .map((message) => message.content as string);
  }, [messages]);

  // Subscribe to real-time messages
  const chatsQuery = useMemo(() => getChatById(chat?.id ?? ''), [chat?.id]);

  // Sync initial chat state when prop changes
  useEffect(() => {
    setRealtimeChat(chat);
  }, [chat]);

  useEffect(() => {
    if (!chat?.id) return;

    const unsubscribe = onSnapshot(chatsQuery, (snapshot) => {
      const data = snapshot.data();
      if (data) {
        // Update the complete chat document
        setRealtimeChat({ ...data, id: snapshot.id } as ChatDocument);
        setMessages(data?.messages ?? []);

        // Scroll to bottom when new messages arrive
        setTimeout(() => {
          if (messageWindowRef.current) {
            messageWindowRef.current.scrollTop =
              messageWindowRef.current.scrollHeight;
          }
        }, 100);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [chatsQuery, chat?.id]);

  // Scroll to first matching message when search term changes or messages load
  useEffect(() => {
    if (
      matchingMessageIndices.length > 0 &&
      messages &&
      messageWindowRef.current &&
      searchTerm
    ) {
      const firstMatchIndex = matchingMessageIndices[0];
      const messageElement = messageRefs.current[firstMatchIndex];

      if (messageElement) {
        setTimeout(() => {
          messageElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 300); // Small delay to ensure messages are rendered
      }
    }
  }, [matchingMessageIndices, messages, searchTerm]);

  const onImageClick = (src: string) => {
    let index: number | undefined;
    const images: { thumb: string; full: string; download_url?: string }[] = [];

    messages
      ?.filter((m) => m.is_img)
      .forEach((msg, i) => {
        if (
          index === undefined &&
          (src === msg.thumbnail || src === msg.content)
        ) {
          index = i;
        }
        images.push({
          thumb: msg.thumbnail ?? msg.content,
          full: msg.content,
          download_url: msg.download_url,
        });
      });

    if (index === undefined) index = 0;
    setImageModal({ images, index, isOpen: true });
  };

  const handleSendMessage = async () => {
    if (!userDoc || !chat?.id || convertingImages) return;

    // Send text message if there's text content
    if (inputMessage.trim()) {
      const messageDoc: ChatMessageDocument = {
        uid: userDoc.uid,
        content: inputMessage.trim(),
        created_at: Date.now(),
        is_img: false,
        is_auto: false,
      };
      sendMessage(messageDoc, chat.id, product || undefined);
    }

    // Send image messages if there are files
    if (fileList.length > 0) {
      const files = Array.from(fileList);
      files.forEach(async (file) => {
        try {
          const uploadResult = await uploadChatImage(
            file,
            userDoc.uid,
            chat.id
          );
          const { path, download_url } = uploadResult;
          const { full, thumb } = getCdnImageUrls(path);

          const imageMessageDoc: ChatMessageDocument = {
            uid: userDoc.uid,
            content: full,
            thumbnail: thumb,
            download_url: download_url,
            created_at: Date.now(),
            is_img: true,
            is_auto: false,
          };

          sendMessage(imageMessageDoc, chat.id, product || undefined);
        } catch (error) {
          logError(error);
          showErrorToast('Failed to upload image');
        }
      });
    }

    // Reset form
    setInputMessage('');
    setFileList([]);
  };

  const handleAttachment = () => {
    const input = document.querySelector(
      `#file-input-${inputId.current}`
    ) as HTMLInputElement;
    if (input) {
      input.click();
    }
  };

  const handlePreviewImageClick = (index: number) => {
    const previewImages = fileList.map((file) => ({
      full: URL.createObjectURL(file),
      thumb: URL.createObjectURL(file),
      download_url: URL.createObjectURL(file),
    }));
    setImageModal({ images: previewImages, index, isOpen: true });
  };

  const processFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      const newFiles = Array.from(files);
      const anyHeic = anyAreHeic(newFiles);
      const { heicTo } = anyHeic ? await import('heic-to') : {};

      if (anyHeic && heicTo) {
        setConvertingImages(true);
        const convertedFiles = await Promise.all(
          newFiles.map(async (file) => {
            if (/image\/hei(c|f)/.test(file.type)) {
              try {
                file = await convertHeic(heicTo, file);
              } catch (e) {
                logError(e);
                showErrorToast('Error converting HEIC image');
              }
            }
            return file;
          })
        );
        setFileList((prevFiles) => [...prevFiles, ...convertedFiles]);
        setConvertingImages(false);
      } else {
        setFileList((prevFiles) => [...prevFiles, ...newFiles]);
      }
    },
    [showErrorToast]
  );

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    await processFiles(Array.from(e.target.files));

    // Clear the file input so the same file can be selected again if needed
    e.target.value = '';
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      const imageFiles = files.filter((file) => file.type.startsWith('image/'));

      if (imageFiles.length > 0) {
        await processFiles(imageFiles);
      }
    },
    [processFiles]
  );

  const isSupportChat = !!chat.case_num;
  const isLoggedInAsSupport = userDoc?.roles?.support || userDoc?.roles?.admin;
  const otherPartyUser = chat.seller_id === userDoc?.uid ? buyerDoc : sellerDoc;
  const isMobileDevice = isMobile() || isTablet();
  const hasPreview = !!product || !!offer || !!order;
  const isTalkingToDeletedUser =
    sellerDoc?.username.startsWith('deleted-') ||
    buyerDoc?.username.startsWith('deleted-');

  const { status } = useOnlineStatus(otherPartyUser?.uid ?? '');

  // Track which chats have had their preview loaded to prevent re-animation
  const chatPreviewKey = `${chat.id}-${product?.id || offer?.id || order?.id}`;
  const isFirstTimeLoading =
    hasPreview && !loadedChatPreviews.has(chatPreviewKey);

  const showPreviewMessages =
    userDoc?.uid === 'FjBH0nwiHgGV4Y0EXDQS' &&
    chat.messages?.[0]?.content.includes('New Boot Trade-In Request');

  // Mark chat preview as loaded when it appears for the first time
  useEffect(() => {
    if (hasPreview && !loadedChatPreviews.has(chatPreviewKey)) {
      setLoadedChatPreviews((prev) => new Set(prev).add(chatPreviewKey));
    }
  }, [hasPreview, chatPreviewKey, loadedChatPreviews]);

  // Auto-focus chat input when chat changes
  useEffect(() => {
    const timer = setTimeout(() => {
      chatInputRef.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, [chat.id]);

  return (
    <LayoutGroup>
      <div
        className={cn(
          'relative flex h-full bg-white',
          isSidebar ? 'w-[55rem]' : 'w-full'
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Left Column - Chat Messages */}
        <motion.div
          layout
          className="relative flex h-full flex-1 flex-col"
          transition={{
            type: 'spring',
            stiffness: 200,
            damping: 25,
            duration: 0.8,
          }}
        >
          <div
            className={cn(
              'h-fit bg-white px-4 py-6 sm:p-8',
              !hasPreview && 'border-b border-brand-lightest-gray'
            )}
          >
            <div className="flex items-center gap-4">
              {/* Back button for mobile */}
              {isMobileDevice && onGoBack && (
                <button
                  type="button"
                  onClick={onGoBack}
                  className="flex h-12 w-12 items-center justify-center rounded-full hover:bg-zinc-100 lg:hidden"
                  aria-label="Go back to conversations"
                >
                  <ChevronLeftIcon className="h-8 w-8 text-zinc-600" />
                </button>
              )}

              <button
                type="button"
                className="flex flex-1 flex-col gap-y-8"
                aria-label="View conversation details"
                onClick={() => {
                  if (isMobileDevice) {
                    setShowMobileChatDetailsModal(true);
                  } else {
                    setShowChatDetailsModal(true);
                  }
                }}
              >
                <div className="group flex items-center gap-4">
                  {isSellerLoading ||
                  isBuyerLoading ||
                  !sellerDoc ||
                  !buyerDoc ? (
                    <div className="h-24 w-24 animate-pulse rounded-full bg-zinc-100" />
                  ) : (
                    <>
                      {isSupportChat && !isLoggedInAsSupport ? (
                        <div className="h-24 w-24 rounded-full bg-black p-4">
                          <LogoWithoutText
                            className="h-full w-full"
                            fill="white"
                          />
                        </div>
                      ) : (
                        <Avatar
                          user={
                            chat.seller_id === userDoc?.uid
                              ? buyerDoc
                              : sellerDoc
                          }
                          circle
                          className="h-24 w-24"
                        />
                      )}
                    </>
                  )}

                  {isSellerLoading ? (
                    <div className="flex flex-col gap-4">
                      <div className="h-4 w-48 animate-pulse rounded-lg bg-zinc-200" />

                      <div className="flex items-center gap-1">
                        <div className="h-5 w-5 animate-pulse rounded-full bg-zinc-200" />

                        <div className="h-4 w-64 animate-pulse rounded-lg bg-zinc-200" />
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-y-2">
                      <div className="flex items-center gap-2">
                        {isSupportChat && !isLoggedInAsSupport ? (
                          <h3 className="text-[1.6rem] font-semibold">
                            MXLocker Support
                          </h3>
                        ) : (
                          <h3
                            className={cn(
                              'max-w-[30rem] truncate text-left text-[1.6rem] font-semibold underline',
                              isTalkingToDeletedUser && 'text-red-600'
                            )}
                          >
                            {isTalkingToDeletedUser
                              ? 'Deleted User'
                              : otherPartyUser?.username}
                          </h3>
                        )}

                        <ChevronRightIcon className="h-8 w-8 text-zinc-500 opacity-0 transition-opacity group-hover:opacity-100" />
                      </div>

                      {isSupportChat && !isLoggedInAsSupport ? (
                        <span className="text-[1.5rem] font-medium text-zinc-500">
                          Our agents typically answer within 24 hours
                        </span>
                      ) : (
                        <StatusLabel
                          status={status}
                          hide_status={!!otherPartyUser?.hide_status}
                        />
                      )}
                    </div>
                  )}
                </div>
              </button>
            </div>

            {/* Support Actions */}
            {(userDoc?.roles?.admin || userDoc?.roles?.support) && (
              <div className="mt-4 px-2 pb-2">
                <SupportActionsRenderer
                  layout={isSidebar ? 'dropdown' : 'horizontal'}
                  userDoc={userDoc}
                  recipientDoc={otherPartyUser}
                  chat={chat}
                  setChat={() => {}}
                  isAdmin={userDoc?.roles?.admin || false}
                  isSupport={
                    userDoc?.roles?.support || userDoc?.roles?.admin || false
                  }
                />
              </div>
            )}

            {showPreviewMessages && (
              <div className="flex gap-4 overflow-x-scroll whitespace-pre-wrap bg-transparent py-4 sm:flex-wrap sm:overflow-auto">
                <button
                  type="button"
                  className="rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage('How much credit are you looking for?');
                  }}
                >
                  How much credit are you looking for?
                </button>
                <button
                  type="button"
                  className="rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage('What boots do you want to go into?');
                  }}
                >
                  What boots do you want to go into?
                </button>
                <button
                  type="button"
                  className="rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage(
                      'How much credit are you looking for? What boots do you want to go into?'
                    );
                  }}
                >
                  How much credit are you looking for? What boots do you want to
                  go into?
                </button>
                <button
                  type="button"
                  className="rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage(
                      'Please confirm once paid, need full name and address.'
                    );
                  }}
                >
                  Please confirm once paid, need full name and address.
                </button>
                <button
                  type="button"
                  className="rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage(
                      'Mind leaving us a review? https://www.trustpilot.com/review/mxlocker.com'
                    );
                  }}
                >
                  Mind leaving us a review?
                  https://www.trustpilot.com/review/mxlocker.com
                </button>
              </div>
            )}
          </div>

          {/* Top Section - Mobile Chat Preview (shown on mobile OR in sidebar mode) */}
          <div className={cn('mt-4 flex', isSidebar ? '' : 'sm:hidden')}>
            <MobileChatPreview
              product={product}
              offer={offer}
              order={order}
              userDoc={userDoc}
              isLoading={isProductLoading || isOfferLoading || isOrderLoading}
            />
          </div>

          {/* Messages */}
          <div
            ref={messageWindowRef}
            className="flex w-full flex-1 overflow-y-auto px-8 pt-8"
          >
            <div className="flex w-full grow flex-col justify-start gap-6 pb-8">
              <div className="mx-auto flex items-center justify-center gap-[1.6rem] rounded-[2rem] bg-zinc-100 px-6 py-[1.6rem] text-[1.4rem]">
                <div>
                  <LogoWithoutText className="h-16 w-16 lg:h-[2.8rem] lg:w-[2.8rem]" />
                </div>

                {product?.category === 'Dirt Bikes'
                  ? `MX Locker is not responsible for the sale of dirt bikes. Beware of scammers when selling outside the platform.`
                  : `To protect your payment, always communicate and pay through the MX Locker website or app.`}
              </div>

              <div className="h-full w-full pt-8">
                <div className="w-full pb-8">
                  {messages?.map((message, i) => {
                    const showDate =
                      i === 0 ||
                      new Date(message.created_at).toLocaleDateString() !==
                        new Date(
                          messages[i - 1].created_at
                        ).toLocaleDateString();

                    return (
                      <React.Fragment
                        key={`${message.created_at}-${message.uid}-${i}`}
                      >
                        {showDate && (
                          <div className="flex w-full items-center justify-center py-8">
                            <p className="sticky top-5 self-center text-[1.5rem] text-zinc-500">
                              {new Date(message.created_at).toLocaleDateString(
                                undefined,
                                {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                }
                              )}
                            </p>
                          </div>
                        )}

                        <div
                          className="mb-4"
                          ref={(el) => {
                            messageRefs.current[i] = el;
                          }}
                        >
                          <MessageBubble
                            chat={chat}
                            chatMessage={message}
                            realtimeMessages={messages}
                            onImageClick={onImageClick}
                            offer={offer}
                            isSidebar={isSidebar}
                          />
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Chat Input Container */}
          <div className="flex-shrink-0">
            <ChatInput
              ref={chatInputRef}
              message={inputMessage}
              setMessage={setInputMessage}
              onSend={handleSendMessage}
              onAttachment={handleAttachment}
              placeholder="Write your message here..."
              fileList={fileList}
              onFileListChange={setFileList}
              convertingImages={convertingImages}
              onImageClick={handlePreviewImageClick}
              chatId={realtimeChat.id}
              onDropFiles={processFiles}
              isSupport={userDoc?.roles?.support}
            />
          </div>

          <input
            id={`file-input-${inputId.current}`}
            type="file"
            accept="image/*"
            className="hidden"
            multiple={true}
            onChange={handleFileChange}
          />
        </motion.div>

        {/* Right Column - Preview Sidebar */}
        {!isSidebar && (
          <div className="hidden sm:flex">
            <ChatPreviewSidebar
              product={product}
              offer={offer}
              userDoc={userDoc}
              order={order}
              isFirstTimeLoading={isFirstTimeLoading}
              chat={realtimeChat}
              isLoading={isProductLoading || isOfferLoading || isOrderLoading}
            />
          </div>
        )}

        <ChatDetailsModal
          key={realtimeChat.id}
          open={showChatDetailsModal}
          dismiss={() => setShowChatDetailsModal(false)}
          images={chatImages}
          chat={realtimeChat}
          userDoc={userDoc}
          sellerDoc={sellerDoc}
          buyerDoc={buyerDoc}
          onArchiveSuccess={onChatArchived}
        />

        <MobileChatDetailsModal
          show={showMobileChatDetailsModal}
          dismiss={() => setShowMobileChatDetailsModal(false)}
          images={chatImages}
          chat={realtimeChat}
          userDoc={userDoc}
          onArchiveSuccess={onChatArchived}
          sellerDoc={sellerDoc}
          buyerDoc={buyerDoc}
        />

        {imageModal.isOpen && (
          <ProductViewerModal
            isOpen={imageModal.isOpen}
            dismiss={() => setImageModal({ ...imageModal, isOpen: false })}
            images={imageModal.images}
            initialImage={imageModal.index}
          />
        )}

        {/* Drag and Drop Overlay */}
        {isDragging && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-zinc-50/80 backdrop-blur-sm">
            <div className="flex flex-col items-center justify-center gap-6 rounded-3xl border-2 border-dashed border-zinc-200 bg-white/90 px-12 py-16 text-center shadow-xl">
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-red-100">
                <CloudUploadIcon className="h-12 w-12 text-brand-secondary" />
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900">
                  Drop images here
                </h3>
                <p className="mt-2 text-gray-600">
                  Release to upload your images to the chat
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </LayoutGroup>
  );
}
